'use client';
import React, { useState, useRef, useEffect } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// ─── Language list ───────────────────────────────────────────────────────────
const LANGUAGES = [
  'Afrikaans', 'Albanian', 'Amharic', 'Arabic', 'Armenian', 'Azerbaijani',
  'Basque', 'Belarusian', 'Bengali', 'Bosnian', 'Bulgarian', 'Catalan',
  'Cebuano', 'Chinese (Simplified)', 'Chinese (Traditional)', 'Corsican',
  'Croatian', 'Czech', 'Danish', 'Dutch', 'English', 'Esperanto', 'Estonian',
  'Finnish', 'French', 'Frisian', 'Galician', 'Georgian', 'German', 'Greek',
  'Gujarati', 'Haitian Creole', 'Hausa', 'Hawaiian', 'Hebrew', 'Hindi',
  'Hmong', 'Hungarian', 'Icelandic', 'Igbo', 'Indonesian', 'Irish', 'Italian',
  'Japanese', 'Javanese', 'Kannada', 'Kazakh', 'Khmer', 'Kinyarwanda',
  'Korean', 'Kurdish', 'Kyrgyz', 'Lao', 'Latin', 'Latvian', 'Lithuanian',
  'Luxembourgish', 'Macedonian', 'Malagasy', 'Malay', 'Malayalam', 'Maltese',
  'Maori', 'Marathi', 'Mongolian', 'Myanmar (Burmese)', 'Nepali', 'Norwegian',
  'Nyanja (Chichewa)', 'Odia (Oriya)', 'Pashto', 'Persian', 'Polish',
  'Portuguese', 'Punjabi', 'Romanian', 'Russian', 'Samoan', 'Scots Gaelic',
  'Serbian', 'Sesotho', 'Shona', 'Sindhi', 'Sinhala (Sinhalese)', 'Slovak',
  'Slovenian', 'Somali', 'Spanish', 'Sundanese', 'Swahili', 'Swedish',
  'Tagalog (Filipino)', 'Tajik', 'Tamil', 'Tatar', 'Telugu', 'Thai', 'Turkish',
  'Turkmen', 'Ukrainian', 'Urdu', 'Uyghur', 'Uzbek', 'Vietnamese', 'Welsh',
  'Xhosa', 'Yiddish', 'Yoruba', 'Zulu',
];

// ─── Content types ────────────────────────────────────────────────────────────
const CONTENT_TYPES = [
  { id: 'documents', label: 'Documents & Reports', icon: '📄' },
  { id: 'marketing', label: 'Marketing & Advertising', icon: '📢' },
  { id: 'legal', label: 'Legal & Compliance', icon: '⚖️' },
  { id: 'technical', label: 'Technical & Software', icon: '💻' },
  { id: 'medical', label: 'Medical & Life Sciences', icon: '🏥' },
  { id: 'ecommerce', label: 'E-commerce & Retail', icon: '🛒' },
  { id: 'training', label: 'AI Training Data', icon: '🤖' },
  { id: 'multimedia', label: 'Multimedia & Video', icon: '🎬' },
  { id: 'other', label: 'Other', icon: '📦' },
];

// ─── Service tiers ────────────────────────────────────────────────────────────
const SERVICE_TIERS = [
  {
    id: 'ai-basic',
    name: 'AI Basic',
    subtitle: 'AI-Powered Translation',
    description: 'AI-powered translation with basic quality checks. Best for high-volume, time-sensitive content.',
    features: ['AI-powered translation', 'Basic quality checks', 'Standard turnaround', 'Glossary support'],
    badge: null,
  },
  {
    id: 'verify-plus',
    name: 'Verify+',
    subtitle: 'AI Translation with Human Verification',
    description: 'AI translation reviewed and verified by human linguists. Ideal for marketing, legal, and brand-critical content.',
    features: ['AI-powered translation', 'Human verification', 'Brand voice consistency', 'Dedicated PM'],
    badge: 'Most Popular',
  },
  {
    id: 'fully-human',
    name: 'Fully Human',
    subtitle: 'Pure Human Translation',
    description: 'End-to-end translation by certified human linguists with no AI involvement. Ideal for sensitive, regulated, or highly nuanced content requiring the highest accuracy and cultural fidelity.',
    features: ['Certified human linguists', 'No AI involvement', 'Regulatory-grade accuracy', 'Cultural adaptation'],
    badge: null,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    subtitle: 'Full Language Operations',
    description: 'End-to-end multilingual program management including LLM evaluation, RLHF, and AI safety testing.',
    features: ['Full LangOps suite', 'LLM evaluation & RLHF', 'Multilingual AI safety testing', 'SLA guarantees'],
    badge: null,
  },
];

// ─── Volume options ───────────────────────────────────────────────────────────
const VOLUME_OPTIONS = [
  'Under 10,000 words/month',
  '10,000 – 50,000 words/month',
  '50,000 – 200,000 words/month',
  '200,000 – 500,000 words/month',
  '500,000+ words/month',
  'Project-based (one-off)',
];

// ─── Step definitions ─────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: 'Languages', shortLabel: '1' },
  { id: 1, label: 'Content', shortLabel: '2' },
  { id: 2, label: 'Service Tier', shortLabel: '3' },
  { id: 3, label: 'Your Details', shortLabel: '4' },
];

// ─── Step icons ───────────────────────────────────────────────────────────────
function LanguagesIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m5 8 6 6" />
      <path d="m4 14 6-6 2-3" />
      <path d="M2 5h12" />
      <path d="M7 2h1" />
      <path d="m22 22-5-10-5 10" />
      <path d="M14 18h6" />
    </svg>
  );
}
function ContentIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}
function ServiceTierIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
function UserIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function ArrowRightIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}
function ArrowLeftIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
function LoaderIcon() {
  return (
    <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  );
}
function CircleCheckIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

// ─── Language Dropdown ────────────────────────────────────────────────────────
interface LanguageDropdownProps {
  label: string;
  selected: string[];
  onChange: (langs: string[]) => void;
  multi?: boolean;
}

function LanguageDropdown({ label, selected, onChange, multi = true }: LanguageDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = LANGUAGES.filter(l =>
    l.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (lang: string) => {
    if (multi) {
      if (selected.includes(lang)) {
        onChange(selected.filter(l => l !== lang));
      } else {
        onChange([...selected, lang]);
      }
    } else {
      onChange([lang]);
      setOpen(false);
      setSearch('');
    }
  };

  const remove = (lang: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selected.filter(l => l !== lang));
  };

  const displayText = selected.length === 0
    ? 'Select languages...'
    : selected.length === 1
      ? selected[0]
      : `${selected.length} languages selected`;

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium mb-2" style={{ color: 'oklch(95% .005 265)' }}>
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg text-sm transition-colors"
        style={{
          backgroundColor: 'rgb(8, 11, 18)',
          border: '1px solid rgba(31, 34, 39, 0.8)',
          color: selected.length > 0 ? 'oklch(95% .005 265)' : 'oklch(65% .01 265)',
        }}
      >
        <span style={{ color: selected.length > 0 ? 'oklch(95% .005 265)' : 'rgba(160,160,180,0.5)' }}>
          {displayText}
        </span>
        <span
          className="transition-transform duration-200"
          style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        >
          <ChevronDownIcon />
        </span>
      </button>

      {/* Selected tags */}
      {selected.length > 0 && multi && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selected.map(lang => (
            <span
              key={lang}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{
                backgroundColor: 'rgba(43, 98, 239, 0.15)',
                border: '1px solid rgba(43, 98, 239, 0.3)',
                color: 'rgb(85, 136, 251)',
              }}
            >
              {lang}
              <button onClick={(e) => remove(lang, e)} className="ml-0.5 hover:opacity-70 transition-opacity">
                <XIcon />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg overflow-hidden"
          style={{
            backgroundColor: 'rgb(19, 22, 28)',
            border: '1px solid rgba(31, 34, 39, 0.8)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            maxHeight: '240px',
          }}
        >
          <div className="p-2" style={{ borderBottom: '1px solid rgba(31,34,39,0.5)' }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search languages..."
              className="w-full px-3 py-1.5 rounded text-sm outline-none"
              style={{
                backgroundColor: 'rgba(8, 11, 18, 0.8)',
                border: '1px solid rgba(31, 34, 39, 0.6)',
                color: 'oklch(95% .005 265)',
              }}
              autoFocus
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: '190px' }}>
            {filtered.length === 0 ? (
              <div className="px-4 py-3 text-sm" style={{ color: 'oklch(65% .01 265)' }}>No languages found</div>
            ) : (
              filtered.map(lang => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => toggle(lang)}
                  className="w-full flex items-center justify-between px-4 py-2 text-sm text-left transition-colors"
                  style={{
                    color: selected.includes(lang) ? 'rgb(85, 136, 251)' : 'oklch(95% .005 265)',
                    backgroundColor: selected.includes(lang) ? 'rgba(43, 98, 239, 0.1)' : 'transparent',
                  }}
                  onMouseEnter={e => {
                    if (!selected.includes(lang)) {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(255,255,255,0.05)';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!selected.includes(lang)) {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {lang}
                  {selected.includes(lang) && (
                    <span style={{ color: 'rgb(43, 98, 239)' }}><CheckIcon /></span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 1: Languages ────────────────────────────────────────────────────────
interface Step1Props {
  sourceLangs: string[];
  targetLangs: string[];
  onSourceChange: (langs: string[]) => void;
  onTargetChange: (langs: string[]) => void;
  onNext: () => void;
}

function Step1Languages({ sourceLangs, targetLangs, onSourceChange, onTargetChange, onNext }: Step1Props) {
  const canContinue = sourceLangs.length > 0 && targetLangs.length > 0;
  return (
    <div className="space-y-6" style={{ opacity: 1, transform: 'none' }}>
      <div>
        <h2 className="font-display font-semibold text-xl mb-2" style={{ color: 'oklch(95% .005 265)' }}>Language Pairs</h2>
        <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>Select your source and target languages. You can choose multiple targets.</p>
      </div>
      <LanguageDropdown
        label="Source Language(s)"
        selected={sourceLangs}
        onChange={onSourceChange}
        multi
      />
      <LanguageDropdown
        label="Target Language(s)"
        selected={targetLangs}
        onChange={onTargetChange}
        multi
      />
      <div
        className="flex items-center justify-between mt-8 pt-6"
        style={{ borderTop: '1px solid rgba(31, 34, 39, 0.5)' }}
      >
        <div />
        <button
          type="button"
          disabled={!canContinue}
          onClick={onNext}
          className="flex items-center gap-2 px-7 py-2.5 text-white font-semibold rounded-lg transition-all text-sm"
          style={{
            backgroundColor: canContinue ? 'rgb(43, 98, 239)' : 'rgba(43, 98, 239, 0.4)',
            cursor: canContinue ? 'pointer' : 'not-allowed',
            opacity: canContinue ? 1 : 0.5,
            boxShadow: canContinue ? '0 0 0px rgba(43,98,239,0)' : 'none',
          }}
        >
          Continue
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Step 2: Content ──────────────────────────────────────────────────────────
interface Step2Props {
  selectedTypes: string[];
  volume: string;
  onTypesChange: (types: string[]) => void;
  onVolumeChange: (vol: string) => void;
  onNext: () => void;
  onBack: () => void;
}

function Step2Content({ selectedTypes, volume, onTypesChange, onVolumeChange, onNext, onBack }: Step2Props) {
  const canContinue = selectedTypes.length > 0;

  const toggleType = (id: string) => {
    if (selectedTypes.includes(id)) {
      onTypesChange(selectedTypes.filter(t => t !== id));
    } else {
      onTypesChange([...selectedTypes, id]);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-semibold text-xl mb-2" style={{ color: 'oklch(95% .005 265)' }}>Content Type</h2>
        <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>What type of content do you need translated or processed?</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {CONTENT_TYPES.map(ct => {
          const active = selectedTypes.includes(ct.id);
          return (
            <button
              key={ct.id}
              type="button"
              onClick={() => toggleType(ct.id)}
              className="flex flex-col items-start gap-2 p-4 rounded-xl text-left transition-all"
              style={{
                backgroundColor: active ? 'rgba(43, 98, 239, 0.12)' : 'rgba(19, 22, 28, 0.8)',
                border: active ? '1px solid rgba(43, 98, 239, 0.5)' : '1px solid rgba(31, 34, 39, 0.6)',
                color: active ? 'oklch(95% .005 265)' : 'oklch(65% .01 265)',
              }}
            >
              <span className="text-xl">{ct.icon}</span>
              <span className="text-sm font-medium leading-tight">{ct.label}</span>
            </button>
          );
        })}
      </div>

      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: 'oklch(95% .005 265)' }}>Estimated Volume</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {VOLUME_OPTIONS.map(vol => {
            const active = volume === vol;
            return (
              <button
                key={vol}
                type="button"
                onClick={() => onVolumeChange(vol)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-left transition-all"
                style={{
                  backgroundColor: active ? 'rgba(43, 98, 239, 0.12)' : 'rgba(8, 11, 18, 0.8)',
                  border: active ? '1px solid rgba(43, 98, 239, 0.5)' : '1px solid rgba(31, 34, 39, 0.6)',
                  color: active ? 'oklch(95% .005 265)' : 'oklch(65% .01 265)',
                }}
              >
                {active && <span style={{ color: 'rgb(43, 98, 239)' }}><CheckIcon /></span>}
                {vol}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="flex items-center justify-between mt-8 pt-6"
        style={{ borderTop: '1px solid rgba(31, 34, 39, 0.5)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid rgba(31, 34, 39, 0.6)',
            color: 'oklch(65% .01 265)',
          }}
        >
          <ArrowLeftIcon />
          Back
        </button>
        <button
          type="button"
          disabled={!canContinue}
          onClick={onNext}
          className="flex items-center gap-2 px-7 py-2.5 text-white font-semibold rounded-lg transition-all text-sm"
          style={{
            backgroundColor: canContinue ? 'rgb(43, 98, 239)' : 'rgba(43, 98, 239, 0.4)',
            cursor: canContinue ? 'pointer' : 'not-allowed',
            opacity: canContinue ? 1 : 0.5,
          }}
        >
          Continue
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Step 3: Service Tier ─────────────────────────────────────────────────────
interface Step3Props {
  selectedTier: string;
  onTierChange: (tier: string) => void;
  onNext: () => void;
  onBack: () => void;
}

function Step3ServiceTier({ selectedTier, onTierChange, onNext, onBack }: Step3Props) {
  const canContinue = selectedTier !== '';
  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-semibold text-xl mb-2" style={{ color: 'oklch(95% .005 265)' }}>Service Tier</h2>
        <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>Choose the level of service that best fits your quality and budget requirements.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {SERVICE_TIERS.map(tier => {
          const active = selectedTier === tier.id;
          return (
            <button
              key={tier.id}
              type="button"
              onClick={() => onTierChange(tier.id)}
              className="relative flex flex-col items-start gap-3 p-5 rounded-xl text-left transition-all"
              style={{
                backgroundColor: active ? 'rgba(43, 98, 239, 0.1)' : 'rgba(19, 22, 28, 0.8)',
                border: active ? '1px solid rgba(43, 98, 239, 0.5)' : '1px solid rgba(31, 34, 39, 0.6)',
              }}
            >
              {tier.badge && (
                <span
                  className="absolute -top-2.5 left-4 px-2.5 py-0.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'rgb(43, 98, 239)', color: 'white' }}
                >
                  {tier.badge}
                </span>
              )}
              <div>
                <div className="font-display font-bold text-base mb-0.5" style={{ color: 'oklch(95% .005 265)' }}>{tier.name}</div>
                <div className="text-xs font-medium" style={{ color: 'rgb(85, 136, 251)' }}>{tier.subtitle}</div>
              </div>
              <p className="text-xs leading-relaxed" style={{ color: 'oklch(65% .01 265)' }}>{tier.description}</p>
              <ul className="space-y-1.5 w-full">
                {tier.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-xs" style={{ color: 'oklch(65% .01 265)' }}>
                    <span style={{ color: 'rgb(43, 98, 239)', flexShrink: 0 }}><CheckIcon /></span>
                    {f}
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

      <div
        className="flex items-center justify-between mt-8 pt-6"
        style={{ borderTop: '1px solid rgba(31, 34, 39, 0.5)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid rgba(31, 34, 39, 0.6)',
            color: 'oklch(65% .01 265)',
          }}
        >
          <ArrowLeftIcon />
          Back
        </button>
        <button
          type="button"
          disabled={!canContinue}
          onClick={onNext}
          className="flex items-center gap-2 px-7 py-2.5 text-white font-semibold rounded-lg transition-all text-sm"
          style={{
            backgroundColor: canContinue ? 'rgb(43, 98, 239)' : 'rgba(43, 98, 239, 0.4)',
            cursor: canContinue ? 'pointer' : 'not-allowed',
            opacity: canContinue ? 1 : 0.5,
          }}
        >
          Continue
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
}

// ─── Step 4: Your Details ─────────────────────────────────────────────────────
interface Step4Data {
  name: string;
  email: string;
  company: string;
  message: string;
}
interface Step4Props {
  data: Step4Data;
  onChange: (data: Step4Data) => void;
  onSubmit: () => void;
  onBack: () => void;
  status: 'idle' | 'submitting' | 'error';
  errorMsg: string;
}

function Step4Details({ data, onChange, onSubmit, onBack, status, errorMsg }: Step4Props) {
  const canSubmit = data.name.trim() !== '' && data.email.trim() !== '' && data.company.trim() !== '';

  const inputStyle: React.CSSProperties = {
    backgroundColor: 'rgb(8, 11, 18)',
    border: '1px solid rgba(31, 34, 39, 0.8)',
    color: 'oklch(95% .005 265)',
    outline: 'none',
  };
  const labelStyle: React.CSSProperties = {
    color: 'oklch(65% .01 265)',
    fontSize: '0.875rem',
    fontWeight: 500,
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-semibold text-xl mb-2" style={{ color: 'oklch(95% .005 265)' }}>Your Details</h2>
        <p className="text-sm" style={{ color: 'oklch(65% .01 265)' }}>Almost done! Tell us how to reach you and we&apos;ll follow up within 24 hours.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="flex flex-col gap-2">
          <label style={labelStyle}>Full Name *</label>
          <input
            type="text"
            required
            value={data.name}
            onChange={e => onChange({ ...data, name: e.target.value })}
            placeholder="Jane Smith"
            className="w-full px-4 py-3 rounded-lg text-sm transition-all"
            style={inputStyle}
          />
        </div>
        <div className="flex flex-col gap-2">
          <label style={labelStyle}>Work Email *</label>
          <input
            type="email"
            required
            value={data.email}
            onChange={e => onChange({ ...data, email: e.target.value })}
            placeholder="jane@company.com"
            className="w-full px-4 py-3 rounded-lg text-sm transition-all"
            style={inputStyle}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label style={labelStyle}>Company / Organisation *</label>
        <input
          type="text"
          required
          value={data.company}
          onChange={e => onChange({ ...data, company: e.target.value })}
          placeholder="Acme Corp"
          className="w-full px-4 py-3 rounded-lg text-sm transition-all"
          style={inputStyle}
        />
      </div>

      <div className="flex flex-col gap-2">
        <label style={labelStyle}>Additional Notes</label>
        <textarea
          rows={4}
          value={data.message}
          onChange={e => onChange({ ...data, message: e.target.value })}
          placeholder="Any specific requirements, timelines, or questions..."
          className="w-full px-4 py-3 rounded-lg text-sm resize-none transition-all"
          style={inputStyle}
        />
      </div>

      {status === 'error' && (
        <div
          className="px-4 py-3 rounded-lg text-sm"
          style={{
            borderColor: 'rgba(239, 68, 68, 0.3)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: 'rgb(252, 165, 165)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
          }}
        >
          {errorMsg}
        </div>
      )}

      <div
        className="flex items-center justify-between mt-8 pt-6"
        style={{ borderTop: '1px solid rgba(31, 34, 39, 0.5)' }}
      >
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
          style={{
            backgroundColor: 'transparent',
            border: '1px solid rgba(31, 34, 39, 0.6)',
            color: 'oklch(65% .01 265)',
          }}
        >
          <ArrowLeftIcon />
          Back
        </button>
        <button
          type="button"
          disabled={!canSubmit || status === 'submitting'}
          onClick={onSubmit}
          className="flex items-center gap-2 px-7 py-2.5 text-white font-semibold rounded-lg transition-all text-sm"
          style={{
            backgroundColor: canSubmit && status !== 'submitting' ? 'rgb(43, 98, 239)' : 'rgba(43, 98, 239, 0.4)',
            cursor: canSubmit && status !== 'submitting' ? 'pointer' : 'not-allowed',
            opacity: canSubmit && status !== 'submitting' ? 1 : 0.5,
          }}
        >
          {status === 'submitting' ? (
            <><LoaderIcon /> Sending…</>
          ) : (
            <>Get My Quote <ArrowRightIcon /></>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Success screen ───────────────────────────────────────────────────────────
function SuccessScreen() {
  return (
    <div className="text-center py-12">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
        style={{
          backgroundColor: 'rgba(0, 210, 148, 0.1)',
          border: '1px solid rgba(0, 210, 148, 0.3)',
          color: 'rgb(167, 243, 208)',
        }}
      >
        <CircleCheckIcon />
      </div>
      <h2 className="font-display font-bold text-2xl mb-3" style={{ color: 'oklch(95% .005 265)' }}>Quote Request Received!</h2>
      <p className="mb-8 max-w-md mx-auto" style={{ color: 'oklch(65% .01 265)' }}>
        Thank you for reaching out. Our team will review your requirements and get back to you within 24 hours with a tailored proposal.
      </p>
      <a
        href="/"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold transition-all"
        style={{ backgroundColor: 'rgb(43, 98, 239)' }}
      >
        Back to Home
      </a>
    </div>
  );
}

// ─── Step icons map ───────────────────────────────────────────────────────────
const STEP_ICONS = [<LanguagesIcon key="lang" />, <ContentIcon key="content" />, <ServiceTierIcon key="tier" />, <UserIcon key="user" />];

// ─── Main page ────────────────────────────────────────────────────────────────
export default function GetAQuotePage() {
  const [currentStep, setCurrentStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  // Step 1 state
  const [sourceLangs, setSourceLangs] = useState<string[]>([]);
  const [targetLangs, setTargetLangs] = useState<string[]>([]);

  // Step 2 state
  const [contentTypes, setContentTypes] = useState<string[]>([]);
  const [volume, setVolume] = useState('');

  // Step 3 state
  const [serviceTier, setServiceTier] = useState('');

  // Step 4 state
  const [details, setDetails] = useState({ name: '', email: '', company: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    setSubmitStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch('/api/submit-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: details.name,
          email: details.email,
          company: details.company,
          service: serviceTier,
          languages: `Source: ${sourceLangs.join(', ')} → Target: ${targetLangs.join(', ')}`,
          volume,
          message: `Content types: ${contentTypes.join(', ')}\n\n${details.message}`,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Submission failed');
      }
      setSubmitted(true);
    } catch (err: unknown) {
      setSubmitStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(8, 11, 18)' }}>
      <Navbar />

      <section className="pt-32 pb-28 lg:pt-40 lg:pb-36">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-12">
            <span
              className="font-mono text-sm tracking-widest uppercase mb-4 block"
              style={{ color: 'rgb(43, 98, 239)', letterSpacing: '0.24em' }}
            >
              GET A QUOTE
            </span>
            <h1
              className="font-display font-bold text-4xl sm:text-5xl leading-tight mb-4"
              style={{ color: 'oklch(95% .005 265)' }}
            >
              Get Your AI Operations{' '}
              <span className="gradient-text">Quote</span>
            </h1>
            <p
              className="text-lg max-w-xl mx-auto"
              style={{ color: 'oklch(65% .01 265)' }}
            >
              Tell us about your project and get a ballpark estimate in under 2 minutes. We will follow up with a detailed proposal.
            </p>
          </div>

          {/* Step indicators */}
          {!submitted && (
            <div className="flex items-center justify-center gap-2 mb-12">
              {STEPS.map((step, idx) => {
                const isActive = idx === currentStep;
                const isDone = idx < currentStep;
                const isDisabled = idx > currentStep;
                return (
                  <div key={step.id} className="flex items-center">
                    <button
                      type="button"
                      disabled={isDisabled}
                      onClick={() => { if (!isDisabled) setCurrentStep(idx); }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
                      style={{
                        backgroundColor: isActive
                          ? 'rgb(43, 98, 239)'
                          : isDone
                            ? 'rgba(43, 98, 239, 0.15)'
                            : 'rgba(31, 34, 39, 0.4)',
                        border: isActive
                          ? 'none'
                          : isDone
                            ? '1px solid rgba(43, 98, 239, 0.3)'
                            : '1px solid rgba(31, 34, 39, 0.5)',
                        color: isActive
                          ? 'white'
                          : isDone
                            ? 'rgb(85, 136, 251)'
                            : 'oklch(65% .01 265)',
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {STEP_ICONS[idx]}
                      <span className="hidden sm:inline">{step.label}</span>
                      <span className="sm:hidden">{step.shortLabel}</span>
                    </button>
                    {idx < STEPS.length - 1 && (
                      <div
                        className="w-8 h-px mx-1"
                        style={{ backgroundColor: 'rgba(31, 34, 39, 0.4)' }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Card */}
          <div className="glow-card rounded-2xl p-8 lg:p-10">
            {submitted ? (
              <SuccessScreen />
            ) : (
              <>
                {currentStep === 0 && (
                  <Step1Languages
                    sourceLangs={sourceLangs}
                    targetLangs={targetLangs}
                    onSourceChange={setSourceLangs}
                    onTargetChange={setTargetLangs}
                    onNext={() => setCurrentStep(1)}
                  />
                )}
                {currentStep === 1 && (
                  <Step2Content
                    selectedTypes={contentTypes}
                    volume={volume}
                    onTypesChange={setContentTypes}
                    onVolumeChange={setVolume}
                    onNext={() => setCurrentStep(2)}
                    onBack={() => setCurrentStep(0)}
                  />
                )}
                {currentStep === 2 && (
                  <Step3ServiceTier
                    selectedTier={serviceTier}
                    onTierChange={setServiceTier}
                    onNext={() => setCurrentStep(3)}
                    onBack={() => setCurrentStep(1)}
                  />
                )}
                {currentStep === 3 && (
                  <Step4Details
                    data={details}
                    onChange={setDetails}
                    onSubmit={handleSubmit}
                    onBack={() => setCurrentStep(2)}
                    status={submitStatus}
                    errorMsg={errorMsg}
                  />
                )}
              </>
            )}
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
